import type {OrgSuccessorBannerPayload} from '@github-ui/code-view-types'
import {useDismissOrgNotice} from '@github-ui/repo-layout'
import {Banner, Link} from '@primer/react'
import {useRef, useState} from 'react'

import {restoreFocusAfterDismiss} from '../restore-focus-after-dismiss'

const ORG_SUCCESSOR_NOTICE = 'add_successor_prompt'
export const HELP_URL =
  'https://docs.github.com/github/setting-up-and-managing-organizations-and-teams/maintaining-ownership-continuity-for-your-organization'

interface OrgSuccessorBannerProps extends OrgSuccessorBannerPayload {
  returnFocusIds?: string[]
}

export function OrgSuccessorBanner({
  organizationId,
  organizationLogin,
  membersPath,
  returnFocusIds,
}: OrgSuccessorBannerProps) {
  const [dismissed, setDismissed] = useState(false)
  const bannerRef = useRef<HTMLElement>(null)
  const {dismissNotice} = useDismissOrgNotice(ORG_SUCCESSOR_NOTICE, organizationLogin, String(organizationId))

  const onDismiss = async () => {
    try {
      if (await dismissNotice()) {
        if (bannerRef.current?.contains(document.activeElement)) {
          restoreFocusAfterDismiss(returnFocusIds)
        }
        setDismissed(true)
      }
    } catch {
      // Leave the banner in place so the viewer can retry.
    }
  }

  if (dismissed) return null

  return (
    <Banner
      ref={bannerRef}
      variant="info"
      title="Ensure the future of your work! Consider adding another owner to this organization."
      description={
        <>
          <Link inline href={HELP_URL}>
            Adding another organization owner
          </Link>{' '}
          helps ensure the continuity of your work in case you are unable to access your account.
        </>
      }
      onDismiss={onDismiss}
      flush
      primaryAction={
        <Banner.PrimaryAction as="a" href={membersPath}>
          View organization members
        </Banner.PrimaryAction>
      }
    />
  )
}
